import { CONTRACT_STATUS } from '../constants/status.ts';
import type { BillingFCP } from './billing.ts';
import FlexBalance from './flex-balance.ts';
import {
  getTimeSince,
  getTimeUntil,
  sevenDays,
  thirtyDays,
} from '../helpers/time.js';

import type { Billing20201105FlexibleConsumptionBillingMethodDetails } from '@clients/cloud-billing';

export default class Flex {
  #fcp?: BillingFCP | null;
  #flexibleConsumptionBillingMethodDetails?: Billing20201105FlexibleConsumptionBillingMethodDetails | null;
  #hasFlexPayment: boolean;

  /**
   * @param fcp - Flex contracts and properties
   * @param flexibleConsumptionBillingMethodDetails - Flex billing method details
   * @param hasFlexPayment - Whether the account has flex payment
   */
  constructor(
    fcp?: BillingFCP | null,
    flexibleConsumptionBillingMethodDetails?: Billing20201105FlexibleConsumptionBillingMethodDetails | null,
    hasFlexPayment?: boolean,
  ) {
    this.#fcp = fcp ?? null;
    this.#flexibleConsumptionBillingMethodDetails =
      flexibleConsumptionBillingMethodDetails ?? null;
    this.#hasFlexPayment = hasFlexPayment ?? false;
  }

  //Flex Properties
  get balance() {
    return this.#hasFlexPayment
      ? new FlexBalance(
          this.latestContract?.flexDetails,
          this.#flexibleConsumptionBillingMethodDetails?.estimatedRemainingBalance,
        )
      : null;
  }

  /** Returns a contract with a future start date or null if there are no upcoming contracts */
  get upcomingContract() {
    return this.#fcp?.upcomingContract || null;
  }

  /** Returns the most recent contract that has already started, including expired contracts if they exist. */
  get latestContract() {
    return this.#fcp?.latestContract || null;
  }

  /** Returns true if there are expired contracts, indicating the active contract is a renewal. */
  get hasExpiredContract() {
    return !!this.#fcp?.hasExpiredContract;
  }

  /** Returns true if a renewal was recently activated (within seven days). */
  get renewalRecentlyActivated() {
    if (
      this.latestContract &&
      this.latestContract.status === CONTRACT_STATUS.ACTIVE &&
      this.hasExpiredContract
    ) {
      const timeSinceActivation = getTimeSince(this.latestContract.activeFrom);
      return (
        this.#hasFlexPayment &&
        !!timeSinceActivation?.milliseconds &&
        timeSinceActivation.milliseconds <= sevenDays
      );
    }
    return false;
  }

  /** Returns true if the contract is expiring within thirty days. */
  get contractExpiringSoon() {
    if (this.latestContract) {
      const timeUntilExpiration = getTimeUntil(this.latestContract.activeUntil);
      return (
        this.#hasFlexPayment &&
        !!timeUntilExpiration?.milliseconds &&
        timeUntilExpiration.milliseconds <= thirtyDays &&
        timeUntilExpiration.milliseconds > 0
      );
    }
    return false;
  }

  /** Returns true if the contract is expired. */
  get contractExpired() {
    if (this.latestContract) {
      return (
        this.#hasFlexPayment &&
        this.latestContract.status === CONTRACT_STATUS.EXPIRED
      );
    }
    return false;
  }

  /** Returns true if the contract is expired, depleted, and has no renewal. */
  get expiredDepletedNoRenewal() {
    return (
      !!this.contractExpired && !this.upcomingContract && !!this.balance?.isZero
    );
  }

  /** Returns true if the contract is depleted and has no renewal. */
  get depletedNoRenewal() {
    return !this.upcomingContract && !!this.balance?.isZero;
  }
}
